import * as React from "react"
import { useState } from "react"
import Switch from "@imminently/decisively-ui/Switch"
import { Label } from "@/ui/primitives/label"

import {
  ComponentPage,
  ComponentSection,
  ComponentPreview,
  ControlPanel,
  ControlRow,
  VariantGrid,
  VariantItem,
} from "../components/ComponentPage"

export function SwitchPage() {
  const [checked, setChecked] = useState(false)
  const [disabled, setDisabled] = useState(false)

  return (
    <ComponentPage
      title="Switch"
      description="A control that allows the user to toggle between checked and not checked."
    >
      {/* Interactive Playground */}
      <ComponentSection
        title="Playground"
        description="Experiment with switch configurations"
      >
        <div className="grid gap-6 lg:grid-cols-2">
          <ComponentPreview>
            <div className="flex items-center space-x-2">
              <Switch
                id="playground-switch"
                checked={checked}
                onCheckedChange={setChecked}
                disabled={disabled}
              />
              <Label htmlFor="playground-switch">
                {checked ? "Enabled" : "Disabled"}
              </Label>
            </div>
          </ComponentPreview>

          <ControlPanel>
            <ControlRow label="Checked">
              <Switch checked={checked} onCheckedChange={setChecked} />
            </ControlRow>
            <ControlRow label="Disabled">
              <Switch checked={disabled} onCheckedChange={setDisabled} />
            </ControlRow>
          </ControlPanel>
        </div>
      </ComponentSection>

      {/* States */}
      <ComponentSection title="States" description="All switch states">
        <ComponentPreview className="gap-8">
          <VariantGrid columns={4}>
            <VariantItem label="Off">
              <Switch />
            </VariantItem>
            <VariantItem label="On">
              <Switch checked />
            </VariantItem>
            <VariantItem label="Disabled Off">
              <Switch disabled />
            </VariantItem>
            <VariantItem label="Disabled On">
              <Switch checked disabled />
            </VariantItem>
          </VariantGrid>
        </ComponentPreview>
      </ComponentSection>

      {/* With Label */}
      <ComponentSection title="With Label" description="Switch with associated labels">
        <ComponentPreview className="flex-col gap-4 items-start">
          <div className="flex items-center space-x-2">
            <Switch id="airplane-mode" />
            <Label htmlFor="airplane-mode">Airplane Mode</Label>
          </div>
          <div className="flex items-center space-x-2">
            <Switch id="notifications" defaultChecked />
            <Label htmlFor="notifications">Enable Notifications</Label>
          </div>
          <div className="flex items-center space-x-2">
            <Switch id="dark-mode" disabled />
            <Label htmlFor="dark-mode" className="text-muted-foreground">
              Dark Mode (Disabled)
            </Label>
          </div>
        </ComponentPreview>
      </ComponentSection>

      {/* Settings Example */}
      <ComponentSection title="Settings Example" description="Switch in a settings context">
        <ComponentPreview>
          <div className="w-full max-w-md space-y-6">
            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label>Marketing emails</Label>
                <p className="text-sm text-muted-foreground">
                  Receive emails about new products, features, and more.
                </p>
              </div>
              <Switch />
            </div>
            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label>Security emails</Label>
                <p className="text-sm text-muted-foreground">
                  Receive emails about your account security.
                </p>
              </div>
              <Switch defaultChecked />
            </div>
            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label>Push notifications</Label>
                <p className="text-sm text-muted-foreground">
                  Receive push notifications on your device.
                </p>
              </div>
              <Switch />
            </div>
          </div>
        </ComponentPreview>
      </ComponentSection>

      {/* Form Example */}
      <ComponentSection title="Form Example" description="Switch in a form">
        <ComponentPreview>
          <form className="w-full max-w-sm space-y-4">
            <div className="rounded-lg border p-4 space-y-4">
              <h4 className="text-sm font-medium">Privacy Settings</h4>
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <Label htmlFor="public-profile" className="text-sm">
                    Public profile
                  </Label>
                  <Switch id="public-profile" />
                </div>
                <div className="flex items-center justify-between">
                  <Label htmlFor="show-email" className="text-sm">
                    Show email
                  </Label>
                  <Switch id="show-email" />
                </div>
                <div className="flex items-center justify-between">
                  <Label htmlFor="show-activity" className="text-sm">
                    Show activity
                  </Label>
                  <Switch id="show-activity" defaultChecked />
                </div>
              </div>
            </div>
          </form>
        </ComponentPreview>
      </ComponentSection>
    </ComponentPage>
  )
}
